import React from "react";
// eslint-disable-next-line object-curly-newline
import { Fade, Slide, Roll, Bounce } from "react-awesome-reveal";
import Header from "./Header.js";
import Footer from "./Footer.js";
import Discuss from "./Discuss.js";

const CoreServicesLineComponent = () => {
  const serviceLines = [
    {
      title: "Tax & Accounting Solutions",
      icon: "📊",
      description:
        "Comprehensive tax planning, preparation, and accounting services for businesses of all sizes",
      features: [
        "Tax Planning & Strategy",
        "Financial Reporting",
        "Bookkeeping Services",
        "Audit Support",
      ],
      revenue: "$2.5M",
      growth: "+25%",
      gradient: "from-emerald-400 to-teal-600",
      bgPattern: "dots",
    },
    {
      title: "Business Consulting",
      icon: "💼",
      description:
        "Strategic guidance for business growth, operational efficiency, and market expansion",
      features: [
        "Strategic Planning",
        "Process Optimization",
        "Market Analysis",
        "Growth Strategy",
      ],
      revenue: "$1.8M",
      growth: "+32%",
      gradient: "from-blue-400 to-indigo-600",
      bgPattern: "grid",
    },
    {
      title: "Venture Funding & Investment",
      icon: "🚀",
      description:
        "Capital raising, investor relations, and funding strategy for growing businesses",
      features: [
        "Capital Raising",
        "Investor Matching",
        "Due Diligence",
        "Valuation Services",
      ],
      revenue: "$3.2M",
      growth: "+45%",
      gradient: "from-purple-400 to-pink-600",
      bgPattern: "waves",
    },
    {
      title: "Compliance & Legal Support",
      icon: "⚖️",
      description:
        "Regulatory compliance, legal documentation, and risk management services",
      features: [
        "Regulatory Compliance",
        "Legal Documentation",
        "Risk Assessment",
        "Policy Development",
      ],
      revenue: "$1.5M",
      growth: "+18%",
      gradient: "from-orange-400 to-red-600",
      bgPattern: "circles",
    },
    {
      title: "Digital Transformation",
      icon: "🌐",
      description:
        "Technology integration, digital strategy, and automation solutions for modern businesses",
      features: [
        "Tech Integration",
        "Digital Strategy",
        "Automation Solutions",
        "System Optimization",
      ],
      revenue: "$2.1M",
      growth: "+55%",
      gradient: "from-cyan-400 to-blue-600",
      bgPattern: "triangles",
    },
    {
      title: "International Expansion",
      icon: "🌍",
      description:
        "Cross-border business setup, international compliance, and global market entry strategies",
      features: [
        "Market Entry Strategy",
        "International Compliance",
        "Cross-border Setup",
        "Global Partnerships",
      ],
      revenue: "$1.9M",
      growth: "+28%",
      gradient: "from-green-400 to-emerald-600",
      bgPattern: "hexagons",
    },
  ];

  return (
    <>
      <Header />

      {/* Hero Section */}
      <div className="relative flex items-center min-h-screen overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-30">
            <div className="absolute text-4xl delay-300 top-1/4 left-1/4 animate-bounce">
              💼
            </div>
            <div className="absolute text-3xl delay-700 top-1/3 right-1/4 animate-bounce">
              📊
            </div>
            <div className="absolute text-5xl delay-1000 bottom-1/3 left-1/3 animate-bounce">
              🚀
            </div>
            <div className="absolute text-3xl delay-500 bottom-1/4 right-1/3 animate-bounce">
              ⚖️
            </div>
            <div className="absolute text-4xl delay-200 top-1/2 left-1/2 animate-bounce">
              🌐
            </div>
          </div>

          {/* Gradient Orbs */}
          <div className="absolute w-64 h-64 rounded-full top-20 left-20 bg-gradient-to-r from-cyan-400 to-blue-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
          <div className="absolute delay-1000 rounded-full bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-400 to-pink-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
          <div className="absolute delay-500 transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-emerald-400 to-teal-600 mix-blend-multiply filter blur-xl opacity-70 animate-pulse" />
        </div>

        <div className="container relative z-10 px-6 mx-auto text-center text-white">
          <Roll triggerOnce>
            <div className="inline-block mb-8">
              <div className="flex items-center justify-center w-32 h-32 mx-auto text-6xl transition-transform duration-700 transform shadow-2xl bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl rotate-12 hover:rotate-0">
                💎
              </div>
            </div>
          </Roll>

          <Fade direction="up" triggerOnce duration={1000}>
            <h1 className="mb-6 text-6xl font-black leading-tight text-transparent md:text-8xl bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text">
              CORE SERVICES
            </h1>
          </Fade>

          <Fade direction="up" delay={300} triggerOnce>
            <h2 className="mb-8 text-3xl font-light md:text-4xl text-cyan-200">
              & Revenue Streams
            </h2>
          </Fade>

          <Fade direction="up" delay={600} triggerOnce>
            <p className="max-w-4xl mx-auto mb-12 text-xl leading-relaxed text-gray-300 md:text-2xl">
              Comprehensive business solutions designed to drive growth, ensure
              compliance, and maximize your market potential across diverse
              revenue channels
            </p>
          </Fade>

          <Bounce delay={1000} triggerOnce>
            <div className="inline-flex items-center px-8 py-4 space-x-3 border rounded-full bg-white/10 backdrop-blur-md border-white/20">
              <span className="text-lg font-medium">$12M+ Annual Revenue</span>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500">
                <span className="text-xs text-white">✓</span>
              </div>
            </div>
          </Bounce>
        </div>
      </div>

      {/* Service Lines Section */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-6 mx-auto">
          <Fade direction="up" triggerOnce>
            <div className="mb-20 text-center">
              <h3 className="mb-6 text-5xl font-bold text-gray-800">
                Our Service Portfolio
              </h3>
              <div className="w-32 h-1 mx-auto mb-8 bg-gradient-to-r from-cyan-500 to-purple-500" />
              <p className="max-w-3xl mx-auto text-xl text-gray-600">
                Six comprehensive service lines delivering measurable value and
                sustainable growth
              </p>
            </div>
          </Fade>

          {/* Services Grid */}
          <div className="grid gap-8 mb-20 lg:grid-cols-2 xl:grid-cols-3">
            {serviceLines.map((service, index) => (
              <Slide
                key={service.title}
                direction={
                  // eslint-disable-next-line no-nested-ternary
                  index % 3 === 0 ? "left" : index % 3 === 1 ? "up" : "right"
                }
                delay={index * 100}
                triggerOnce
              >
                <div className="relative overflow-hidden transition-all duration-500 transform bg-white border border-gray-100 shadow-xl group rounded-3xl hover:shadow-2xl hover:-translate-y-6">
                  {/* Background Gradient */}
                  <div className="absolute inset-0 opacity-5">
                    <div
                      className={`w-full h-full bg-gradient-to-br ${service.gradient}`}
                    />
                  </div>

                  {/* Revenue Badge */}
                  <div className="absolute z-20 top-4 right-4">
                    <div
                      className={`bg-gradient-to-r ${service.gradient} text-white px-3 py-1 rounded-full text-xs font-bold`}
                    >
                      {service.growth}
                    </div>
                  </div>

                  <div className="relative z-10 p-8">
                    <div className="mb-6 text-6xl transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-12">
                      {service.icon}
                    </div>
                    <h4 className="mb-4 text-2xl font-bold text-gray-800 group-hover:text-gray-900">
                      {service.title}
                    </h4>
                    <p className="mb-6 leading-relaxed text-gray-600 group-hover:text-gray-700">
                      {service.description}
                    </p>

                    <div className="mb-6 space-y-2">
                      {service.features.map((feature) => (
                        <div
                          key={feature}
                          className="flex items-center space-x-2"
                        >
                          <div
                            className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.gradient}`}
                          />
                          <span className="text-sm text-gray-600">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-800">
                        Annual Revenue:
                      </span>
                      <span
                        className={`text-2xl font-black bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}
                      >
                        {service.revenue}
                      </span>
                    </div>
                  </div>

                  {/* Bottom Accent Line */}
                  <div
                    className={`absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r ${service.gradient}`}
                  />
                </div>
              </Slide>
            ))}
          </div>
        </div>
      </div>

      <Discuss />
      <Footer />
    </>
  );
};

export default CoreServicesLineComponent;
